import React, { FC, useMemo, memo } from 'react';
import { ReactComponent as ClockIcon } from '@blink/assets/src/icons/clock.svg';
import { ReactComponent as ArrowIcon } from '@blink/assets/src/icons/arrowBtn.svg';

import { AssetsToBorrowItemRow } from './AssetsToBorrowItemRow';

import { EmptyBanner } from '@/widgets/Banners/EmptyBanner';
import { ROLE } from '@/shared/api/borrow';
import { Button, ButtonVariant, SkeletonRow } from '@/shared/ui';
import { useHandleNavigation } from '@/features/Pools/hooks/useHandleNavigation';
import { useFetchAgreements } from '@/shared/hooks/useFetchAgreements';
import { ServerError } from '@/widgets/ServerError';
import { useHasAgreementError } from '@/shared/hooks/useErrors';
import { getAgreementsAction } from '@/shared/store/agreements/actions';

export const AssetsToBorrowSection: FC = memo(() => {
    const { agreements, agreementsLoading } = useFetchAgreements(ROLE.BORROWER);
    const handleClick = useHandleNavigation();
    const hasError = useHasAgreementError();

    const borrowerAgreements = useMemo(
        () => agreements.filter((item) => item.role === ROLE.BORROWER),
        [agreements],
    );
    const isEmpty = useMemo(
        () => borrowerAgreements.length === 0,
        [borrowerAgreements, agreementsLoading],
    );

    const getContent = () => {
        if (agreementsLoading && isEmpty) {
            return Array.from({ length: 6 }, (_, i) => <SkeletonRow key={i} />);
        }

        return borrowerAgreements.map((item, index) => (
            <AssetsToBorrowItemRow
                key={index}
                itemIndex={index}
                allItems={borrowerAgreements}
                role={ROLE.BORROWER}
            />
        ));
    };

    return (
        <div className='w-full mt-[52px]'>
            <div className='flex items-center justify-between'>
                <h2 className='pl-5 pb-5 text-lg font-semibold'>Assets to Borrow</h2>
                <div className='flex items-center gap-7 px-6 pb-6'>
                    <span className='text-gray-500 text-md'>
                        We provide personalised pools upon request
                    </span>
                    <Button
                        variant={ButtonVariant.Ghost}
                        className='flex items-center gap-2'
                        name={'borrow-request-personalised-pool'}
                        onClick={handleClick}
                    >
                        <ArrowIcon /> Request personalised pool
                    </Button>
                </div>
            </div>

            {hasError ? (
                <ServerError action={getAgreementsAction} />
            ) : isEmpty ? (
                <EmptyBanner
                    customTitle='You don’t have any whitelisted pools yet.'
                    customSubtitle='Please check back later or contact support if you need help with accessing pools.'
                    icon={ClockIcon}
                />
            ) : (
                <div className='overflow-x-auto'>
                    <table className='w-full border-collapse border border-opaque min-w-[950px]'>
                        <thead>
                            <tr className='border-b border-gray-300 h-[52px]'>
                                <th className='w-1/5 bg-background-secondary text-left pl-5 font-semibold'>
                                    Pools
                                </th>
                                <th className='w-1/5 bg-background-secondary text-left font-semibold'>
                                    Asset
                                </th>
                                <th className='w-1/5 bg-background-secondary text-left font-semibold'>
                                    Available Liquidity
                                </th>
                                <th className='w-1/5 bg-background-secondary text-left font-semibold'>
                                    Borrow APY
                                </th>
                                <th className='w-1/5 bg-background-secondary text-left font-semibold'>
                                    Your Wallet
                                </th>
                                <th className='w-1/5 bg-background-secondary text-left font-semibold' />
                            </tr>
                        </thead>
                        <tbody data-testid='user-borrows-table'>{getContent()}</tbody>
                    </table>
                </div>
            )}
        </div>
    );
});
